import { Component, computed, inject, Input, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService } from '../../../core/services/toast.service';
import { VendorsService } from '../../../core/services/vendors.service';
import { DirtyTrackingService } from '../../../core/services/dirty-tracking.service';
import { authActions } from '../../../core/store/auth';
import { ApiError } from '../../../core/models/common/api-error';
import { User } from '../../../core/models/entities/user';
import { Vendor } from '../../../core/models/entities/vendor';
import {
  getEditableLocales,
  getVendorFallbackLocale,
} from '../../../core/services/locale.service';
import { TranslatedFilesState } from '../../../shared-manager/components/translated-file-upload/translated-files-state';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-vendor-terms-settings',
  template: `
    <app-fieldset-card
      [showSaveButton]="true"
      [saving]="saving()"
      [saveButtonDisabled]="filesState.isEmpty()"
      (onSave)="save()"
    >
      <div title>{{ 'settings.vendor.terms.title' | transloco }}</div>
      <div description>
        {{ 'settings.vendor.terms.description' | transloco }}
      </div>

      <app-translation-tabs
        [values]="filesState.tabValues(editableLocales(), currentUrls())"
        [(activeLocale)]="activeLocale"
      ></app-translation-tabs>

      <div class="mt-4">
        <app-translated-file-upload
          [state]="filesState"
          [locale]="activeLocale()"
          [primaryLocale]="primaryLocale()"
          [currentUrls]="currentUrls()"
          [labelSubject]="'settings.vendor.terms.upload-subject' | transloco"
          (changed)="onFileChange()"
        ></app-translated-file-upload>
      </div>
    </app-fieldset-card>
  `,
  imports: [SharedModule, SharedManagerModule],
})
export class VendorTermsSettingsComponent {
  @Input({ required: true }) set authUser(user: User) {
    this.user.set(user);
    // L'utilisateur arrive après le premier rendu : l'onglet initial ne peut
    // suivre sa langue principale qu'une fois celui-ci connu.
    if (user && !this.localeInitialized) {
      this.localeInitialized = true;
      this.activeLocale.set(this.editableLocales()[0]);
    }
  }

  private store = inject(Store);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);
  private vendorsService = inject(VendorsService);
  private dirtyTrackingService = inject(DirtyTrackingService);

  private user = signal<User | null>(null);

  saving = signal<boolean>(false);
  readonly filesState = new TranslatedFilesState();

  editableLocales = computed(() => getEditableLocales(this.user()?.vendor));
  primaryLocale = computed(() => getVendorFallbackLocale(this.user()?.vendor));
  currentUrls = computed(
    () => this.user()?.vendor?.general_terms_public_urls ?? {},
  );
  activeLocale = signal<string>(getVendorFallbackLocale(null));

  private localeInitialized = false;

  onFileChange() {
    this.dirtyTrackingService.setDirty(!this.filesState.isEmpty());
  }

  async save() {
    const files = this.filesState.files();
    const removed = this.filesState.removedLocales();
    this.saving.set(true);
    try {
      // Une requête par langue : chaque langue a son propre fichier côté API.
      for (const locale of removed) {
        this.updateUserStore(
          await this.vendorsService.deleteGeneralTerms(locale),
        );
      }
      for (const [locale, file] of Object.entries(files)) {
        this.updateUserStore(
          await this.vendorsService.uploadGeneralTerms(file, locale),
        );
      }
      this.filesState.clear();
      this.dirtyTrackingService.setDirty(false);
      this.toastService.success(
        this.translocoService.translate('settings.vendor.terms.toast-saved'),
      );
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.saving.set(false);
    }
  }

  private updateUserStore(vendor: Partial<Vendor>) {
    const user = this.user();
    if (!user) {
      return;
    }

    const updated: User = {
      ...user,
      vendor: { ...user.vendor, ...vendor },
    };
    this.user.set(updated);
    this.store.dispatch(authActions.findCurrentUserSuccess({ user: updated }));
  }
}
